import { cn } from '@/lib/utils'
import { CheckIcon } from 'lucide-react'
import React, { Fragment, useEffect } from 'react'
import {
  matchPath,
  NavigateOptions,
  useLocation,
  useNavigate,
} from 'react-router'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '../ui/breadcrumb'
import { Progress } from '../ui/progress'
import _ from 'lodash'

export interface Step {
  title: string
  description?: string
  path: string // This is not a RouteKey, but a fully resolved path
}

// Utility type to extract step paths from a tuple of steps
type StepItem<Steps extends readonly Step[]> = Steps[number]

export interface StepperContextProps<Steps extends readonly Step[]> {
  steps: Steps
  activeStep: StepItem<Steps> | null
  nextStep: StepItem<Steps> | null
  goPrev: () => void
  goNext: () => void
  setStep: (step: StepItem<Steps>, options?: NavigateOptions) => void
}

const StepperContext = React.createContext<
  StepperContextProps<readonly Step[]>
>(undefined!)

type StepperProviderProps<Steps extends readonly Step[]> =
  React.PropsWithChildren<{
    steps: Steps
  }>

export function StepperProvider<Steps extends readonly Step[]>({
  steps,
  children,
}: StepperProviderProps<Steps>) {
  // Hooks
  const { pathname } = useLocation()
  const navigate = useNavigate()

  // State
  const activeStep = React.useMemo(
    () => steps.find((step) => step.path === pathname) || null,
    [pathname, steps]
  )
  const currentIndex = steps.findIndex((step) => step === activeStep)
  const nextStep = React.useMemo(() => {
    const nextIndex = Math.min(currentIndex + 1, steps.length - 1)
    return steps[nextIndex] || null
  }, [currentIndex, steps])

  // Set a specific step by navigating. The incoming step may use either a
  // resolved path (e.g. /.../<uuid>/...) or a template path containing
  // route params (e.g. /.../:applicationId/...), so match both by exact
  // equality and by matchPath pattern.
  const setStep = React.useCallback(
    (step: StepItem<Steps>, options?: NavigateOptions) => {
      const targetStep = steps.find(
        (s) => s.path === step.path || matchPath(step.path, s.path) !== null
      )
      if (targetStep && pathname !== targetStep.path)
        navigate(targetStep.path, options)
    },
    [navigate, pathname, steps]
  )

  // Previous step
  const goPrev = React.useCallback(() => {
    const prevIndex = Math.max(currentIndex - 1, 0)
    setStep(steps[prevIndex], { state: { direction: 'backward' } })
  }, [currentIndex, setStep, steps])

  // Next step
  const goNext = React.useCallback(() => {
    const nextIndex = Math.min(currentIndex + 1, steps.length - 1)
    setStep(steps[nextIndex], { state: { direction: 'forward' } })
  }, [currentIndex, setStep, steps])

  // Navigate to the new step when step changes
  useEffect(() => {
    if (activeStep && pathname !== activeStep.path) navigate(activeStep.path)
  }, [activeStep, navigate, steps, pathname])

  return (
    <StepperContext.Provider
      value={{
        steps,
        activeStep,
        nextStep,
        goPrev,
        goNext,
        setStep,
      }}
    >
      {children}
    </StepperContext.Provider>
  )
}

export function useStepper<Steps extends readonly Step[]>() {
  const context = React.useContext(StepperContext)
  if (!context)
    throw new Error('useStepperContext must be used within StepperProvider')
  return context as unknown as StepperContextProps<Steps>
}

interface StepperProps {
  allComplete?: string | string[] // Path(s) that indicate(s) all steps are complete
  canJumpBack?: boolean // Allow jumping back to specific previous steps
}

const ALL_COMPLETE = 9999

export function Stepper({ allComplete, canJumpBack }: StepperProps) {
  const { pathname } = useLocation()
  const { steps, activeStep, setStep } = useStepper()
  const activeIndex = (() => {
    const completeSteps = Array.isArray(allComplete)
      ? allComplete
      : [allComplete]
    if (completeSteps?.some((p) => p === pathname)) return ALL_COMPLETE // All steps complete
    return steps.findIndex(({ path }) => path === activeStep?.path)
  })()

  return (
    <StepIndicator
      steps={steps}
      activeIndex={activeIndex}
      onStepClick={
        canJumpBack
          ? (index) =>
              activeIndex !== ALL_COMPLETE &&
              index < activeIndex &&
              setStep(steps[index])
          : undefined
      }
    />
  )
}

export interface StepIndicatorStep {
  title: string
  description?: string
}

export interface StepIndicatorProps {
  steps: readonly StepIndicatorStep[]
  activeIndex: number // Index of the current step; steps before it are complete
  onStepClick?: (index: number) => void // Enables jump-back navigation on completed steps
}

// Presentational step indicator (circles, checkmarks, connecting lines) shared
// by the route-driven Stepper above and any caller that derives its own
// activeIndex (e.g. a static status card not tied to route navigation).
export function StepIndicator({
  steps,
  activeIndex,
  onStepClick,
}: StepIndicatorProps) {
  return (
    <div className="flex justify-between">
      {steps.map((step, index) => (
        <div
          key={index}
          className="relative flex flex-1 flex-col items-center gap-2 px-2 text-center"
        >
          {/* Step circle */}
          <div
            className={cn(
              'relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-3 font-semibold tabular-nums transition-colors duration-200 ease-in-out',
              index == activeIndex
                ? // Active step
                  'border-primary text-primary'
                : index < activeIndex
                  ? // Completed step
                    'border-primary bg-primary text-primary-foreground'
                  : // Inactive step
                    'text-muted-foreground border-border',
              onStepClick && index < activeIndex && 'cursor-pointer'
            )}
            onClick={() =>
              onStepClick && index < activeIndex && onStepClick(index)
            }
          >
            {/* Completed, show checkmark */}
            <CheckIcon
              size={20}
              className={cn(
                'absolute stroke-3 transition-transform duration-200 ease-in-out',
                index >= activeIndex && '-translate-x-10'
              )}
            />
            <div
              className={cn(
                'absolute transition-transform duration-200 ease-in-out',
                !(index >= activeIndex) && 'translate-x-10'
              )}
            >
              {index + 1}
            </div>
          </div>

          {/* Step content */}
          <div
            className={cn(
              'flex flex-col items-center gap-1 transition-all duration-200 ease-in-out',
              index == activeIndex ? 'text-primary' : 'text-muted-foreground'
            )}
          >
            <span className="text-base font-medium">{step.title}</span>
            {step.description && (
              <span className="max-w-64 text-sm leading-tight">
                {step.description}
              </span>
            )}
          </div>

          {/* Connecting line */}
          {index < steps.length - 1 && (
            <div
              className={cn(
                'bg-border absolute top-5 left-1/2 h-[3px] w-[calc(100%-calc(var(--spacing)*14))] translate-x-7 transition-all duration-200 ease-in-out',
                index <= activeIndex - 1
                  ? // Active or completed step
                    'bg-primary'
                  : // Inactive step
                    'bg-border'
              )}
            />
          )}
        </div>
      ))}
    </div>
  )
}

export function StepperBasic() {
  const { steps, activeStep, setStep } = useStepper()
  const activeIndex = steps.findIndex(({ path }) => path === activeStep?.path)
  const percentage = _.round(
    activeIndex ? (activeIndex / steps.length) * 100 : 0
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-sm">
          Step {activeIndex + 1} of {steps.length}
        </span>
        <span className="text-sm font-medium">{percentage}%</span>
      </div>

      <Progress value={percentage} max={100} />

      <Breadcrumb>
        <BreadcrumbList>
          {_.map(steps, (s, idx) => (
            <Fragment key={idx}>
              <BreadcrumbItem
                onClick={() => setStep(steps[idx])}
                style={{ fontWeight: activeIndex === idx ? 'bold' : '' }}
              >
                {s.title}
              </BreadcrumbItem>
              {idx < steps.length - 1 && (
                <BreadcrumbSeparator> / </BreadcrumbSeparator>
              )}
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  )
}
